/* ============================================================
   CardRenderer — Maps card type keys to actual React components
   Supports optional onUpdateData for inline editing
   ============================================================ */

import type { CanvasBlock, BlockStyles } from '@/lib/cms/card-registry'
import { blockStylesToCss } from '@/components/cms/BlockStyleEditor'
import ServiceCard from './ServiceCard'
import TeamCard from './TeamCard'
import TestimonialCard from './TestimonialCard'
import HeroBanner from './HeroBanner'
import TextBlock from './TextBlock'
import ImageBlock from './ImageBlock'
import CtaBlock from './CtaBlock'
import EventCard from './EventCard'
import StylistFlipCard from './StylistFlipCard'
import { SpacerBlock, DividerBlock, HeadingBlock } from './LayoutBlocks'
import LooksGalleryBlock from './LooksGalleryBlock'

export interface CardEditCallbacks {
  /** Update one or more data fields on this block */
  onUpdateData?: (updates: Record<string, string>) => void
  /** Whether this block is currently selected */
  isSelected?: boolean
}

interface CardRendererProps {
  block: CanvasBlock
  editing?: CardEditCallbacks
  blockStyles?: BlockStyles
}

// ── Animation CSS generators ──────────────────────────
const ANIMATION_KEYFRAMES: Record<string, string> = {
  fadeIn:    'from { opacity: 0 } to { opacity: 1 }',
  slideUp:   'from { opacity: 0; transform: translateY(30px) } to { opacity: 1; transform: translateY(0) }',
  slideLeft: 'from { opacity: 0; transform: translateX(-40px) } to { opacity: 1; transform: translateX(0) }',
  scaleIn:   'from { opacity: 0; transform: scale(0.85) } to { opacity: 1; transform: scale(1) }',
  bounce:    '0% { opacity: 0; transform: translateY(40px) } 60% { opacity: 1; transform: translateY(-8px) } 100% { transform: translateY(0) }',
}

function getAnimationCss(scopeId: string, anim: string): string {
  const kf = ANIMATION_KEYFRAMES[anim]
  if (!kf) return ''
  const name = `${scopeId}-${anim}`
  return `\n@keyframes ${name} { ${kf} }\n.${scopeId}-anim { animation: ${name} 0.6s ease-out both; }`
}

export default function CardRenderer({ block, editing, blockStyles }: CardRendererProps) {
  const { type, data, entityData } = block
  const entityObj = entityData as Record<string, unknown> | undefined
  const onUpdate = editing?.onUpdateData
  const styleCss = blockStylesToCss(blockStyles)

  let content: React.ReactNode
  switch (type) {
    case 'hero-banner':
      content = <HeroBanner data={data} onUpdateData={onUpdate} />; break
    case 'text-block':
      content = <TextBlock data={data} onUpdateData={onUpdate} />; break
    case 'heading':
      content = <HeadingBlock data={data} onUpdateData={onUpdate} />; break
    case 'cta-block':
      content = <CtaBlock data={data} onUpdateData={onUpdate} />; break
    case 'service-card':
      content = <ServiceCard data={data} entityData={entityObj} />; break
    case 'team-card':
      content = <TeamCard data={data} entityData={entityObj} />; break
    case 'stylist-flip-card':
      content = <StylistFlipCard data={data} entityData={entityObj} />; break
    case 'testimonial-card':
      content = <TestimonialCard data={data} entityData={entityObj} />; break
    case 'event-card':
      content = <EventCard data={data} entityData={entityObj} />; break
    case 'looks-gallery':
      content = <LooksGalleryBlock data={data} onUpdateData={onUpdate} />; break
    case 'image-block':
    case 'gallery-image':
      content = <ImageBlock data={data} />; break
    case 'spacer':
      content = <SpacerBlock data={data} />; break
    case 'divider':
      content = <DividerBlock data={data} />; break
    default:
      content = (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          Unknown card type: {type}
        </div>
      )
  }

  // Always wrap when styles object exists — even "empty" styles matter
  // for typography we need the wrapper to cascade through child elements
  const hasStyles = blockStyles && Object.keys(blockStyles).length > 0
  if (hasStyles && Object.keys(styleCss).length > 0) {
    // Build scoped CSS overrides so typography cascades through card components'
    // own Tailwind classes (e.g. text-white on a <h1> inside HeroBanner)
    const scopeId = `bs-${block.id.slice(0, 8)}`
    const overrides: string[] = []
    if (styleCss.color) overrides.push(`color: ${styleCss.color} !important`)
    if (styleCss.fontFamily) overrides.push(`font-family: ${styleCss.fontFamily} !important`)
    if (styleCss.fontSize) overrides.push(`font-size: ${styleCss.fontSize} !important`)
    if (styleCss.fontWeight) overrides.push(`font-weight: ${styleCss.fontWeight} !important`)
    if (styleCss.textAlign) overrides.push(`text-align: ${styleCss.textAlign} !important`)

    // Strip typography from inline style so we don't double-apply
    // Destructure typography props out since they're applied via scoped CSS, not inline styles
    const { color: _c, fontFamily: _f, fontSize: _fs, fontWeight: _fw, textAlign: _ta, ...layoutStyles } = styleCss

    // ── Animation keyframes ──
    const anim = blockStyles?.animation
    const animCss = anim && anim !== 'none' ? getAnimationCss(scopeId, anim) : ''

    return (
      <>
        {(overrides.length > 0 || animCss) && (
          <style>{`${overrides.length > 0 ? `.${scopeId} h1, .${scopeId} h2, .${scopeId} h3, .${scopeId} p, .${scopeId} span, .${scopeId} a, .${scopeId} li { ${overrides.join('; ')} }` : ''}${animCss}`}</style>
        )}
        <div
          style={{ ...layoutStyles, borderRadius: layoutStyles.borderRadius || undefined }}
          className={`overflow-hidden rounded-xl ${overrides.length > 0 ? scopeId : ''} ${anim && anim !== 'none' ? `${scopeId}-anim` : ''}`}
        >
          {content}
        </div>
      </>
    )
  }

  return <>{content}</>
}
